import { useState, useEffect, useCallback } from 'react'
import type { Network } from './types'
import { prodexer } from './api'
import { useTheme } from './useTheme'
import { WalletExplorer } from './tools/WalletExplorer'
import { BlockLookup } from './tools/BlockLookup'
import { TopicExplorer } from './tools/TopicExplorer'
import { TransactionLookup } from './tools/TransactionLookup'

export type Tab = 'wallet' | 'block' | 'topic' | 'tx'

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: 'wallet', label: 'Wallet Explorer', icon: '💼' },
  { id: 'block', label: 'Block Lookup', icon: '📦' },
  { id: 'topic', label: 'Topic Explorer', icon: '🎯' },
  { id: 'tx', label: 'Transaction Lookup', icon: '🔗' },
]

/** Read initial tab + query from URL search params (?tool=wallet&q=allo1...). */
function readUrlParams(): { tab: Tab; query: string } {
  const params = new URLSearchParams(window.location.search)
  const tool = params.get('tool') as Tab | null
  const query = params.get('q') ?? ''
  const validTabs: Tab[] = ['wallet', 'block', 'topic', 'tx']
  return {
    tab: tool && validTabs.includes(tool) ? tool : 'wallet',
    query,
  }
}

/** Build a deep-link URL for a given tool + query (used by handleTabChange). */
function toolUrl(tool: Tab, query: string): string {
  const params = new URLSearchParams()
  if (tool !== 'wallet') params.set('tool', tool)
  if (query) params.set('q', query)
  const qs = params.toString()
  return qs ? `?${qs}` : window.location.pathname
}

function ThemeToggle() {
  const { theme, toggle } = useTheme()
  return (
    <button
      onClick={toggle}
      className="rounded-lg border border-border-default bg-surface p-2 text-text-muted hover:text-text-primary hover:border-border-strong transition-colors"
      title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
      aria-label="Toggle theme"
    >
      {theme === 'dark' ? (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ) : (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      )}
    </button>
  )
}

function App() {
  const [network, setNetwork] = useState<Network>('mainnet')
  const initial = readUrlParams()
  const [activeTab, setActiveTab] = useState<Tab>(initial.tab)
  const [initialQuery, setInitialQuery] = useState<string>(initial.query)
  const [apiHealthy, setApiHealthy] = useState<boolean | null>(null)

  // Update the singleton client when network changes
  useEffect(() => {
    prodexer.setNetwork(network)
    setApiHealthy(null)
    prodexer.health().then(setApiHealthy)
  }, [network])

  // Update URL when tab changes (without query)
  const handleTabChange = useCallback((tab: Tab) => {
    setActiveTab(tab)
    setInitialQuery('')
    const url = toolUrl(tab, '')
    window.history.pushState(null, '', url)
  }, [])

  // Handle browser back/forward — re-read URL params
  useEffect(() => {
    const handlePopState = () => {
      const params = readUrlParams()
      setActiveTab(params.tab)
      setInitialQuery(params.query)
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary">
      {/* Header */}
      <header className="border-b border-border-default bg-surface/80 backdrop-blur sticky top-0 z-10">
        <div className="mx-auto max-w-7xl px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent text-white font-bold text-sm">
                A
              </div>
              <div>
                <h1 className="text-base font-semibold text-text-bright">
                  Allora Ops Chain Tool
                </h1>
                <p className="text-xs text-text-muted">
                  Allora chain data explorer
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* API health indicator */}
              <div className="flex items-center gap-1.5">
                <div
                  className={`h-2 w-2 rounded-full ${
                    apiHealthy === null
                      ? 'bg-text-faint'
                      : apiHealthy
                      ? 'bg-success'
                      : 'bg-danger'
                  } ${apiHealthy === null ? '' : 'animate-pulse'}`}
                />
                <span className="text-xs text-text-muted">
                  {apiHealthy === null
                    ? 'checking…'
                    : apiHealthy
                    ? 'API reachable'
                    : 'API unreachable'}
                </span>
              </div>

              <ThemeToggle />

              {/* Network switcher */}
              <div className="flex rounded-lg border border-border-default bg-surface p-0.5">
                {(['testnet', 'mainnet'] as Network[]).map((net) => (
                  <button
                    key={net}
                    onClick={() => setNetwork(net)}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                      network === net
                        ? 'bg-accent text-white'
                        : 'text-text-muted hover:text-text-primary'
                    }`}
                  >
                    {net.charAt(0).toUpperCase() + net.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Tab navigation */}
      <nav className="border-b border-border-default bg-surface-alt">
        <div className="mx-auto max-w-7xl px-4">
          <div className="flex gap-1">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  activeTab === tab.id
                    ? 'border-accent text-accent-text'
                    : 'border-transparent text-text-muted hover:text-text-primary'
                }`}
              >
                <span className="text-base">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main className="mx-auto max-w-7xl px-4 py-6">
        {activeTab === 'wallet' && <WalletExplorer key={`wallet-${initialQuery}`} network={network} initialQuery={initialQuery} />}
        {activeTab === 'block' && <BlockLookup key={`block-${initialQuery}`} initialQuery={initialQuery} />}
        {activeTab === 'topic' && <TopicExplorer key={`topic-${initialQuery}`} initialQuery={initialQuery} />}
        {activeTab === 'tx' && <TransactionLookup key={`tx-${initialQuery}`} initialQuery={initialQuery} />}
      </main>

      {/* Footer */}
      <footer className="border-t border-border-default bg-surface-alt mt-12">
        <div className="mx-auto max-w-7xl px-4 py-4 text-xs text-text-faint">
          <div className="flex items-center justify-between">
            <span>
              All data is read-only. Powered by the Allora prodexer API.
            </span>
            <span className="font-mono">
              {network} · {prodexer.baseUrl}
            </span>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default App
